/* eslint-disable react-refresh/only-export-components -- co-located exports are intentional */
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/lib/queryClient';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/store/authStore';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/ui/RouteErrorBoundary';
import { initPostHog } from '@/lib/posthog';
import { applyProductBranding } from '@/lib/product-mode';
// Self-hosted Inter (DSGVO). Satoshi @font-face lives in index.css.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import './index.css';
import App from './App.tsx';

// Initialize PostHog analytics (respects cookie consent)
initPostHog();
// D-14: swap favicon + tab title to Feedback OS assets when running on app.feedback-os.com.
// No-op on sellercockpit.com / localhost / *.pages.dev (full shell keeps SellerCockpit defaults).
applyProductBranding();

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const setUser = useAuthStore((state) => state.setUser);
  const setLoading = useAuthStore((state) => state.setLoading);

  useEffect(() => {
    console.log('[AUTH INIT] AuthInitializer mounted, initializing auth state');
    let authEventReceived = false;

    // Listen for auth state changes - this will fire immediately with current session
    const {
      data: { subscription },
    } = authService.onAuthStateChange((user) => {
      console.log('[AUTH INIT] onAuthStateChange event fired', { user: user ? { id: user.id, email: user.email } : null });
      authEventReceived = true;
      setUser(user);
    });

    // Detect if we likely have a session (stored token or OAuth callback in URL hash)
    const hasOAuthCallback = window.location.hash.includes('access_token');
    const hasStoredSession = Object.keys(localStorage).some(k => k.includes('auth-token'));

    // Use a generous timeout when a session is expected (IndexedDB + lock + token refresh can be slow)
    // Use a shorter timeout when no session indicators exist (quick null resolution)
    const timeoutMs = (hasOAuthCallback || hasStoredSession) ? 15000 : 5000;

    const safetyTimeout = setTimeout(() => {
      if (!authEventReceived) {
        console.warn(`[AUTH INIT] No auth event received within ${timeoutMs / 1000}s, forcing loading to false`);
        setLoading(false);
      }
    }, timeoutMs);

    return () => {
      console.log('[AUTH INIT] AuthInitializer unmounting, unsubscribing from auth changes');
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [setUser, setLoading]);

  return <>{children}</>;
}

// Create the router with data router support (required for useBlocker in CallNavigationBlocker)
const router = createBrowserRouter(
  [
    {
      path: '*',
      element: <App />,
      errorElement: <RouteErrorBoundary />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthInitializer>
          <RouterProvider router={router} />
          <Toaster position="top-right" />
        </AuthInitializer>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
